import { getToken } from "@/utils/functions/getToken"
import {
  AdminShop,
  CollaborateurAddBeautyInput,
  CollaborateurAddBeautyOutput,
  CollaborateurDeleteBeautyInput,
  CollaborateurDeleteBeautyOutput,
  CollaborateurUpdateBeautyInput,
  CollaborateurUpdateBeautyOutput,
  Manager,
  Prestatere,
} from "@okeys/shared-utils"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { customAxios } from "../utils/customAxios/customAxios"

// Hook pour ajouter un collaborateur
export const useAddCollaborateur = () => {
  const queryClient = useQueryClient()

  return useMutation<
    CollaborateurAddBeautyOutput,
    Error,
    CollaborateurAddBeautyInput,
    { previousDataShop: unknown }
  >({
    mutationFn: async (input: CollaborateurAddBeautyInput) => {
      const token = await getToken()
      const response = await customAxios.post<CollaborateurAddBeautyOutput>(
        "/data-shop/collaborateur",
        input,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      )
      return response.data
    },
    onMutate: async (input: CollaborateurAddBeautyInput) => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ["shopData"] })

      // Snapshot de l'état précédent pour rollback
      const previousDataShop = queryClient.getQueryData(["shopData"])

      // Mise à jour optimiste du cache
      queryClient.setQueryData(["shopData"], (old: unknown) => {
        if (!old || typeof old !== "object") return old

        const oldData = old as Record<string, unknown>

        // Créer le nouveau collaborateur optimiste
        const optimisticCollaborateur = {
          id: `temp-${Date.now()}`, // ID temporaire
          email: input.collaborateur.email,
          givenName: input.collaborateur.givenName,
          familyName: input.collaborateur.familyName,
          role: input.collaborateur.role,
          isActive: true,
          createdAt: new Date().toISOString(),
          createdBy: "current-user", // À adapter selon votre contexte
          isOptimistic: true, // Flag pour identifier les données optimistes
        }

        // Ajouter le collaborateur au bon groupe selon son rôle
        const updatedData = { ...oldData }

        if (input.collaborateur.role === "manager") {
          const managers = Array.isArray(oldData.managers)
            ? oldData.managers
            : []
          updatedData.managers = [...managers, optimisticCollaborateur]
        } else if (input.collaborateur.role === "admin") {
          const admins = Array.isArray(oldData.admins) ? oldData.admins : []
          updatedData.admins = [...admins, optimisticCollaborateur]
        } else if (input.collaborateur.role === "prestatere") {
          const prestateres = Array.isArray(oldData.prestateres)
            ? oldData.prestateres
            : []
          updatedData.prestateres = [...prestateres, optimisticCollaborateur]
        }

        return updatedData
      })

      // Retourner le contexte pour rollback
      return { previousDataShop }
    },
    onError: (err, _input, context) => {
      // Rollback en cas d'erreur
      if (context?.previousDataShop) {
        queryClient.setQueryData(["shopData"], context.previousDataShop)
      }

      console.error("Erreur lors de l'ajout du collaborateur:", err)
    },
    onSettled: () => {
      // Invalider et refetch pour avoir les vraies données du serveur
      queryClient.invalidateQueries({
        queryKey: ["shopData"],
      })

      // Invalider l'historique de la boutique
      queryClient.invalidateQueries({
        queryKey: ["shopHistoric"],
      })
    },
  })
}

// Hook pour modifier un collaborateur
export const useUpdateCollaborateur = () => {
  const queryClient = useQueryClient()

  return useMutation<
    CollaborateurUpdateBeautyOutput,
    Error,
    CollaborateurUpdateBeautyInput,
    { previousDataShop: unknown }
  >({
    mutationFn: async (input: CollaborateurUpdateBeautyInput) => {
      const token = await getToken()
      const response = await customAxios.put<CollaborateurUpdateBeautyOutput>(
        "/data-shop/collaborateur",
        input,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      )
      return response.data
    },
    onMutate: async (input: CollaborateurUpdateBeautyInput) => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ["shopData"] })

      // Snapshot de l'état précédent pour rollback
      const previousDataShop = queryClient.getQueryData(["shopData"])

      // Mise à jour optimiste du cache
      queryClient.setQueryData(["shopData"], (old: unknown) => {
        if (!old || typeof old !== "object") return old

        const oldData = old as Record<string, unknown>
        const updatedData = { ...oldData }

        // Trouver le collaborateur existant dans toutes les listes
        let existingCollaborateur: Record<string, unknown> | null = null
        let currentRole: string = ""
        let currentListName: string = ""
        let currentIndex: number = -1

        // Chercher dans managers
        if (Array.isArray(oldData.managers)) {
          const index = oldData.managers.findIndex(
            (manager: Record<string, unknown>) =>
              manager.id === input.collaborateurId
          )
          if (index !== -1) {
            existingCollaborateur = oldData.managers[index]
            currentRole = "manager"
            currentListName = "managers"
            currentIndex = index
          }
        }

        // Chercher dans prestataires si pas trouvé
        if (!existingCollaborateur && Array.isArray(oldData.prestateres)) {
          const index = oldData.prestateres.findIndex(
            (prestatere: Record<string, unknown>) =>
              prestatere.id === input.collaborateurId
          )
          if (index !== -1) {
            existingCollaborateur = oldData.prestateres[index]
            currentRole = "prestatere"
            currentListName = "prestateres"
            currentIndex = index
          }
        }

        // Chercher dans admins si pas trouvé
        if (!existingCollaborateur && Array.isArray(oldData.admins)) {
          const index = oldData.admins.findIndex(
            (admin: Record<string, unknown>) =>
              admin.id === input.collaborateurId
          )
          if (index !== -1) {
            existingCollaborateur = oldData.admins[index]
            currentRole = "admin"
            currentListName = "admins"
            currentIndex = index
          }
        }

        if (!existingCollaborateur) {
          console.warn("Collaborateur non trouvé pour mise à jour optimiste")
          return oldData
        }

        // Déterminer le nouveau rôle
        const newRole = input.collaborateur.role

        // Si le rôle change, on doit déplacer le collaborateur
        if (currentRole !== newRole) {
          // Supprimer de l'ancienne liste
          if (currentListName === "managers") {
            updatedData.managers = (
              oldData.managers as Record<string, unknown>[]
            ).filter(
              (item: Record<string, unknown>) =>
                item.id !== input.collaborateurId
            )
          } else if (currentListName === "prestateres") {
            updatedData.prestateres = (
              oldData.prestateres as Record<string, unknown>[]
            ).filter(
              (item: Record<string, unknown>) =>
                item.id !== input.collaborateurId
            )
          } else if (currentListName === "admins") {
            updatedData.admins = (
              oldData.admins as Record<string, unknown>[]
            ).filter(
              (item: Record<string, unknown>) =>
                item.id !== input.collaborateurId
            )
          }

          // Créer le nouveau collaborateur avec le bon format selon le nouveau rôle
          const baseCollaborateur = {
            ...existingCollaborateur,
            schedule: input.collaborateur.schedule,
            updatedAt: new Date().toISOString(),
            isOptimistic: true,
          }

          let newCollaborateur: Partial<Manager | Prestatere | AdminShop>

          if (newRole === "manager") {
            newCollaborateur = {
              ...baseCollaborateur,
              givenName: input.collaborateur.givenName,
              familyName: input.collaborateur.familyName,
              isPrestatere: input.collaborateur.isPrestatere || false,
            }
            // S'assurer que la liste existe
            if (!Array.isArray(updatedData.managers)) {
              updatedData.managers = []
            }
            updatedData.managers = [
              ...(updatedData.managers as Record<string, unknown>[]),
              newCollaborateur,
            ]
          } else if (newRole === "prestatere") {
            newCollaborateur = {
              ...baseCollaborateur,
              givenName: input.collaborateur.givenName,
              familyName: input.collaborateur.familyName,
            }
            // S'assurer que la liste existe
            if (!Array.isArray(updatedData.prestateres)) {
              updatedData.prestateres = []
            }
            updatedData.prestateres = [
              ...(updatedData.prestateres as Record<string, unknown>[]),
              newCollaborateur,
            ]
          } else if (newRole === "admin") {
            newCollaborateur = {
              ...baseCollaborateur,
              givenName: input.collaborateur.givenName,
              familyName: input.collaborateur.familyName,
              isPrestatere: input.collaborateur.isPrestatere || false,
            }
            // S'assurer que la liste existe
            if (!Array.isArray(updatedData.admins)) {
              updatedData.admins = []
            }
            updatedData.admins = [
              ...(updatedData.admins as Record<string, unknown>[]),
              newCollaborateur,
            ]
          }
        } else {
          // Même rôle, juste mettre à jour les données existantes
          if (
            currentListName === "managers" &&
            Array.isArray(updatedData.managers)
          ) {
            updatedData.managers = (
              updatedData.managers as Record<string, unknown>[]
            ).map((item, index) =>
              index === currentIndex
                ? {
                    ...item,
                    givenName: input.collaborateur.givenName,
                    familyName: input.collaborateur.familyName,
                    isPrestatere: input.collaborateur.isPrestatere || false,
                    schedule: input.collaborateur.schedule,
                    updatedAt: new Date().toISOString(),
                    isOptimistic: true,
                  }
                : item
            )
          } else if (
            currentListName === "prestateres" &&
            Array.isArray(updatedData.prestateres)
          ) {
            updatedData.prestateres = (
              updatedData.prestateres as Record<string, unknown>[]
            ).map((item, index) =>
              index === currentIndex
                ? {
                    ...item,
                    givenName: input.collaborateur.givenName,
                    familyName: input.collaborateur.familyName,
                    schedule: input.collaborateur.schedule,
                    updatedAt: new Date().toISOString(),
                    isOptimistic: true,
                  }
                : item
            )
          } else if (
            currentListName === "admins" &&
            Array.isArray(updatedData.admins)
          ) {
            updatedData.admins = (
              updatedData.admins as Record<string, unknown>[]
            ).map((item, index) =>
              index === currentIndex
                ? {
                    ...item,
                    givenName: input.collaborateur.givenName,
                    familyName: input.collaborateur.familyName,
                    isPrestatere: input.collaborateur.isPrestatere || false,
                    schedule: input.collaborateur.schedule,
                    updatedAt: new Date().toISOString(),
                    isOptimistic: true,
                  }
                : item
            )
          }
        }

        return updatedData
      })

      // Retourner le contexte pour rollback
      return { previousDataShop }
    },
    onError: (err, _input, context) => {
      // Rollback en cas d'erreur
      if (context?.previousDataShop) {
        queryClient.setQueryData(["shopData"], context.previousDataShop)
      }

      console.error("Erreur lors de la modification du collaborateur:", err)
    },
    onSuccess: () => {
      // Invalider immédiatement les queries pour forcer un refetch
      queryClient.invalidateQueries({
        queryKey: ["shopData"],
      })

      // Invalider l'historique de la boutique
      queryClient.invalidateQueries({
        queryKey: ["shopHistoric"],
      })
    },
    onSettled: () => {
      // Invalider et refetch pour avoir les vraies données du serveur
      queryClient.invalidateQueries({
        queryKey: ["shopData"],
      })

      // Invalider l'historique de la boutique
      queryClient.invalidateQueries({
        queryKey: ["shopHistoric"],
      })
    },
  })
}

// Hook pour supprimer un collaborateur
export const useDeleteCollaborateur = () => {
  const queryClient = useQueryClient()

  return useMutation<
    CollaborateurDeleteBeautyOutput,
    Error,
    CollaborateurDeleteBeautyInput,
    { previousDataShop: unknown }
  >({
    mutationFn: async (input: CollaborateurDeleteBeautyInput) => {
      const token = await getToken()
      const response =
        await customAxios.delete<CollaborateurDeleteBeautyOutput>(
          "/data-shop/collaborateur",
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
            data: input,
          }
        )
      return response.data
    },
    onMutate: async (input: CollaborateurDeleteBeautyInput) => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ["shopData"] })

      // Snapshot de l'état précédent pour rollback
      const previousDataShop = queryClient.getQueryData(["shopData"])

      // Mise à jour optimiste du cache
      queryClient.setQueryData(["shopData"], (old: unknown) => {
        if (!old || typeof old !== "object") return old

        const oldData = old as Record<string, unknown>
        const updatedData = { ...oldData }

        // Trouver et supprimer le collaborateur de la bonne liste
        let collaborateurFound = false

        // Chercher dans managers
        if (Array.isArray(oldData.managers)) {
          const collaborateurIndex = oldData.managers.findIndex(
            (manager: Record<string, unknown>) =>
              manager.id === input.collaborateurId
          )
          if (collaborateurIndex !== -1) {
            updatedData.managers = oldData.managers.filter(
              (manager: Record<string, unknown>) =>
                manager.id !== input.collaborateurId
            )
            collaborateurFound = true
          }
        }

        // Chercher dans prestataires si pas trouvé
        if (!collaborateurFound && Array.isArray(oldData.prestateres)) {
          const collaborateurIndex = oldData.prestateres.findIndex(
            (prestatere: Record<string, unknown>) =>
              prestatere.id === input.collaborateurId
          )
          if (collaborateurIndex !== -1) {
            updatedData.prestateres = oldData.prestateres.filter(
              (prestatere: Record<string, unknown>) =>
                prestatere.id !== input.collaborateurId
            )
            collaborateurFound = true
          }
        }

        // Chercher dans admins si pas trouvé
        if (!collaborateurFound && Array.isArray(oldData.admins)) {
          const collaborateurIndex = oldData.admins.findIndex(
            (admin: Record<string, unknown>) =>
              admin.id === input.collaborateurId
          )
          if (collaborateurIndex !== -1) {
            updatedData.admins = oldData.admins.filter(
              (admin: Record<string, unknown>) =>
                admin.id !== input.collaborateurId
            )
            collaborateurFound = true
          }
        }

        if (!collaborateurFound) {
          console.warn("Collaborateur non trouvé pour suppression optimiste")
        }

        return updatedData
      })

      // Retourner le contexte pour rollback
      return { previousDataShop }
    },
    onError: (err, _input, context) => {
      // Rollback en cas d'erreur
      if (context?.previousDataShop) {
        queryClient.setQueryData(["shopData"], context.previousDataShop)
      }

      console.error("Erreur lors de la suppression du collaborateur:", err)
    },
    onSuccess: () => {
      // Invalider immédiatement les queries pour forcer un refetch
      queryClient.invalidateQueries({
        queryKey: ["shopData"],
      })

      // Invalider l'historique de la boutique
      queryClient.invalidateQueries({
        queryKey: ["shopHistoric"],
      })
    },
    onSettled: () => {
      // Invalider et refetch pour avoir les vraies données du serveur
      queryClient.invalidateQueries({
        queryKey: ["shopData"],
      })

      // Invalider l'historique de la boutique
      queryClient.invalidateQueries({
        queryKey: ["shopHistoric"],
      })
    },
  })
}

// Hook avec des paramètres typés pour une meilleure DX
export const useCreateCollaborateur = () => {
  const addCollaborateur = useAddCollaborateur()

  return {
    createCollaborateur: (params: CollaborateurAddBeautyInput) => {
      return addCollaborateur.mutate(params)
    },
    isLoading: addCollaborateur.isPending,
    isError: addCollaborateur.isError,
    error: addCollaborateur.error,
    isSuccess: addCollaborateur.isSuccess,
    data: addCollaborateur.data,
    reset: addCollaborateur.reset,
  }
}

// Hook avec des paramètres typés pour la modification
export const useEditCollaborateur = () => {
  const updateCollaborateur = useUpdateCollaborateur()

  return {
    updateCollaborateur: (params: CollaborateurUpdateBeautyInput) => {
      return updateCollaborateur.mutate(params)
    },
    isLoading: updateCollaborateur.isPending,
    isError: updateCollaborateur.isError,
    error: updateCollaborateur.error,
    isSuccess: updateCollaborateur.isSuccess,
    data: updateCollaborateur.data,
    reset: updateCollaborateur.reset,
  }
}

// Hook avec des paramètres typés pour la suppression
export const useRemoveCollaborateur = () => {
  const deleteCollaborateur = useDeleteCollaborateur()

  return {
    deleteCollaborateur: (collaborateurId: string) => {
      return deleteCollaborateur.mutate({ collaborateurId })
    },
    isLoading: deleteCollaborateur.isPending,
    isError: deleteCollaborateur.isError,
    error: deleteCollaborateur.error,
    isSuccess: deleteCollaborateur.isSuccess,
    data: deleteCollaborateur.data,
    reset: deleteCollaborateur.reset,
  }
}
